<template>
  <div>
    <div class="profile">
      <div class="profile__left">
        <div class="profile__title">Authentication</div>
        <div class="profile__caption">
          Configure how users authenticate
        </div>
      </div>
      <div class="profile__right">
        <div class="auth-option">
          <div class="auth-option__label">Email OTP</div>
          <div class="radio--inline">
            <div class="radio__item">
              <input
                class="radio__item__input"
                id="email-otp-on"
                name="emailOtp"
                type="radio"
                v-model="emailOtp"
                value="ON"
                @change="onEmailOtpChange"
              />
              <label class="radio__item__label" for="email-otp-on">
                <span class="radio__item__label__span">On</span>
              </label>
            </div>
            <div class="radio__item">
              <input
                class="radio__item__input"
                id="email-otp-off"
                name="emailOtp"
                type="radio"
                v-model="emailOtp"
                value="OFF"
                @change="onEmailOtpChange"
              />
              <label class="radio__item__label" for="email-otp-off">
                <span class="radio__item__label__span">Off</span>
              </label>
            </div>
          </div>
        </div>
        <div class="auth-option">
          <div class="auth-option__label">MFA</div>
          <div class="radio--inline">
            <div class="radio__item">
              <input
                class="radio__item__input"
                id="mfa-on"
                name="mfa"
                type="radio"
                v-model="mfa"
                value="ON"
                @change="onMfaChange"
              />
              <label class="radio__item__label" for="mfa-on">
                <span class="radio__item__label__span">On</span>
              </label>
            </div>
            <div class="radio__item">
              <input
                class="radio__item__input"
                id="mfa-off"
                name="mfa"
                type="radio"
                v-model="mfa"
                value="OFF"
                @change="onMfaChange"
              />
              <label class="radio__item__label" for="mfa-off">
                <span class="radio__item__label__span">Off</span>
              </label>
            </div>
          </div>
        </div>
        <div class="auth-option">
          <AuthPin
            ref="authPin"
            :containerClass="'pin-input-container-left'"
            :titleClass="'pin-title-sm'"
            :inputContainerClass="'pin-inputs-left'"
            title="Authenticator Code"
            @change="handlePinChange"
          />
        </div>
      </div>
    </div>
    <div class="action">
      <button
        :disabled="disabled"
        @click="saveChanges"
        class="btn btn--primary"
      >
        Save Changes
      </button>
    </div>
  </div>
</template>

<script>
import AuthPin from "@/components/authPin.vue";

export default {
  metaInfo: {
    title: "Settings - Authentication",
  },
  components: {
    AuthPin,
  },
  data() {
    return {
      emailOtp: "OFF",
      mfa: "OFF",
      totpCode: "",
      loading: false,
      fetchingStatus: false,
    };
  },
  computed: {
    otpMethod() {
      if (this.mfa === "ON") return "totp";
      if (this.emailOtp === "ON") return "email_otp";
      return null;
    },
    disabled() {
      return (
        this.loading ||
        this.fetchingStatus ||
        !this.otpMethod ||
        !this.totpCode ||
        this.totpCode.length < 6
      );
    },
  },
  created() {
    this.fetchOtpStatus();
  },
  methods: {
    applyOtpMethod(method) {
      if (method === "totp") {
        this.mfa = "ON";
        this.emailOtp = "OFF";
        return;
      }
      if (method === "email_otp") {
        this.emailOtp = "ON";
        this.mfa = "OFF";
        return;
      }
      this.emailOtp = "OFF";
      this.mfa = "OFF";
    },
    async fetchOtpStatus() {
      this.fetchingStatus = true;
      try {
        const response = await this.$http.get("totp/admin/status");
        const data = (response.body && response.body.data) || response.body || {};
        const method = data.otp_method || data.method || null;
        this.applyOtpMethod(method);
      } catch (error) {
        this.$notify({
          group: "notifications-default",
          type: "error text-white",
          title: "Error",
          text:
            (error.body && error.body.message) ||
            "Failed to load authentication settings",
        });
      } finally {
        this.fetchingStatus = false;
      }
    },
    onEmailOtpChange() {
      if (this.emailOtp === "ON") {
        this.mfa = "OFF";
        return;
      }
      // Keep at least one method on
      if (this.mfa !== "ON") {
        this.emailOtp = "ON";
      }
    },
    onMfaChange() {
      if (this.mfa === "ON") {
        this.emailOtp = "OFF";
        return;
      }
      // Keep at least one method on
      if (this.emailOtp !== "ON") {
        this.mfa = "ON";
      }
    },
    handlePinChange(pin) {
      this.totpCode = pin;
    },
    clearAuthenticatorCode() {
      this.totpCode = "";
      if (this.$refs.authPin) {
        this.$refs.authPin.clear();
      }
    },
    async saveChanges() {
      if (this.disabled) return;

      this.loading = true;
      try {
        const response = await this.$http.put("admin/user/otp-method", {
          otp_method: this.otpMethod,
          totp_code: this.totpCode,
        });
        this.$notify({
          group: "notifications-default",
          type: "success text-white",
          title: "Success",
          text:
            (response.body && response.body.message) ||
            "Authentication settings saved",
        });
        await this.fetchOtpStatus();
      } catch (error) {
        this.$notify({
          group: "notifications-default",
          type: "error text-white",
          title: "Error",
          text:
            (error.body && error.body.message) ||
            "Failed to update authentication settings",
        });
      } finally {
        this.loading = false;
        this.clearAuthenticatorCode();
      }
    },
  },
};
</script>

<style scoped>
.profile__title {
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 27px;
  color: #7a153c;
}

.auth-option {
  margin-bottom: 32px;
}

.auth-option:last-child {
  margin-bottom: 0;
}

.auth-option__label {
  font-size: 14px;
  font-weight: 500;
  color: #323f4b;
  margin-bottom: 12px;
}
</style>
